// components/Comments/CommentAction.jsx
import { useState } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Badge,
  Stack,
  Popover,
  Button
} from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ReplyIcon from '@mui/icons-material/Reply';
import FlagIcon from '@mui/icons-material/Flag';
import { useUser } from '../../contexts/UserContext';

export default function CommentAction({
  comment,
  onEdit,
  onDelete,
  onReply,
  onReport,
  onLike,
  isEditing,
  showReplyForm,
  depth = 0,
  loading = false,
}) {
  const { userInfo } = useUser();
  const isAuthor = userInfo?.username === comment.author_username;
  const likedByMe = !!comment.liked_by_me;
  const likeCnt = comment.like_count ?? 0;

  const [anchorEl, setAnchorEl] = useState(null);
  const openPop = Boolean(anchorEl);
  const confirmDelete = () => {
    setAnchorEl(null);
    onDelete(comment.id);
  };

  const handleLike = () => {
    if (loading) return;
    onLike(comment.id);
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{
        width: '100%',
        flexWrap: 'wrap',           // ✅ 좁은 화면 대응
        rowGap: 0.5,
      }}
    >
      {/* 좋아요 버튼 */}
      <Tooltip title={likedByMe ? '좋아요 취소' : '좋아요'} arrow>
        <span>
          <IconButton
            onClick={handleLike}
            disabled={loading}
            color={likedByMe ? 'primary' : 'default'}
            aria-label="toggle like"
            sx={{
              minWidth: 36,
              minHeight: 36,
            }}
          >
            <Badge
              badgeContent={likeCnt}
              overlap="circular"
              max={999}
              sx={{
                '& .MuiBadge-badge': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                  fontSize: '0.7rem',
                },
              }}
            >
              {likedByMe ? <FavoriteIcon /> : <FavoriteBorderIcon />}
            </Badge>
          </IconButton>
        </span>
      </Tooltip>

      {/* spacer */}
      <Box sx={{ flexGrow: 1 }} />

      {/* 기타 액션 버튼들 */}
      <Stack
        direction="row"
        spacing={{ xs: 0.5, sm: 1 }}
        alignItems="center"
        flexWrap="wrap"
        rowGap={0.5}
      >
        {/* 수정 */}
        {isAuthor && !isEditing && (
          <Tooltip title="수정" arrow>
            <span>
              <IconButton
                size="small"
                onClick={() => onEdit(comment.id, comment.content)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {/* 삭제 */}
        {isAuthor && !isEditing && (
          <>
            <Tooltip title="삭제" arrow>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Popover
              open={openPop}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              sx={{ '& .MuiPaper-root': { width: 220 } }} // ✅ 모바일 대응
            >
              <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                정말 삭제할까요?
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  onClick={confirmDelete}
                >
                  삭제
                </Button>
                <Button
                  size="small"
                  onClick={() => setAnchorEl(null)}
                >
                  취소
                </Button>
              </Box>
            </Popover>
          </>
        )}

        {/* 답글 */}
        {depth === 0 && (
          <Tooltip title={showReplyForm ? '답글 취소' : '답글'} arrow>
            <span>
              <IconButton
                size="small"
                color={showReplyForm ? 'primary' : 'default'}
                onClick={() => onReply(comment.id)}
              >
                <ReplyIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}

        {/* 신고 */}
        <Tooltip title="신고" arrow>
          <span>
            <IconButton
              size="small"
              color="error"
              onClick={() => onReport(comment.id, 'comment')}
            >
              <FlagIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
}

CommentAction.defaultProps = {
  onEdit: () => { },
  onDelete: () => { },
  onReply: () => { },
  onReport: () => { },
  onLike: () => { },
  isEditing: false,
  showReplyForm: false,
  depth: 0,
  loading: false,
};
